import { useNavigate, NavLink } from "react-router-dom";
import classes from "./IPDPatientList.module.css";
import { Table, Button, Spinner, Modal } from "react-bootstrap";
import { useState, useEffect, useContext } from "react";
import { getAuthToken } from "../../../util/auth";
import { getApiChara } from "../../../util/getApi";
import { toast } from "react-toastify";
import { FaSignOutAlt, FaEdit, FaPlay, FaCheck, FaEye, FaFilePdf, FaPrint, FaTimes } from "react-icons/fa";
import AdmissionDetailsModal from "./Pages/AdmissionDetailsModal";
import DischargeModal from "./Pages/DischargeModal";
import DischargeSummaryViewModal from "./Pages/DischargeSummaryViewModal";
import AdmissionStatusNotesModal from "./Pages/AdmissionStatusNotesModal";
import jsPDF from "jspdf";
import "jspdf-autotable";
import { getReportHeader, getReportStyles } from "../../../util/reportPrintHelper";
import AuthContext from "../../../store/auth-context";
import { drawReportHeader, drawAuthorizedSignatory } from "../../../util/headerUtil";
import { useTranslation } from "react-i18next";

const IPDPatientList = () => {
  const { t } = useTranslation();
  const authCtx = useContext(AuthContext);
  const { hospitalData } = authCtx;
  const navigate = useNavigate();

  const getTodayDate = () => {
    const today = new Date();
    return today.toISOString().split("T")[0];
  };

  const getSixMonthsAgoDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 180);
    return date.toISOString().split("T")[0];
  };

  const get15DaysAgoDate = () => {
    const date = new Date();
    date.setDate(date.getDate() - 45);
    return date.toISOString().split("T")[0];
  };

  const [patients, setPatients] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [showDischargeModal, setShowDischargeModal] = useState(false);
  const [showAdmissionModal, setShowAdmissionModal] = useState(false);
  const [selectedAdmission, setSelectedAdmission] = useState(null);
  const [statusFilter, setStatusFilter] = useState("ACTIVE"); // Default to ACTIVE
  const [showViewSummaryModal, setShowViewSummaryModal] = useState(false);
  const [showStatusNotesModal, setShowStatusNotesModal] = useState(false);
  const [selectedDischargedAdmission, setSelectedDischargedAdmission] = useState(null);
  const [patientSearch, setPatientSearch] = useState("");
  const [matchingPatients, setMatchingPatients] = useState([]);
  const [selectedPatientId, setSelectedPatientId] = useState(null);

  // Reporting/Print States
  const [startDate, setStartDate] = useState(get15DaysAgoDate());
  const [endDate, setEndDate] = useState(getTodayDate());
  const [selectedPageSize, setSelectedPageSize] = useState("a4");
  const [selectedOrientation, setSelectedOrientation] = useState("portrait");
  const [showPreview, setShowPreview] = useState(null);

  const fetchPatients = async (status = statusFilter, start = startDate, end = endDate, forceClientFilter = false) => {
    const token = getAuthToken();
    if (!token || token === "EXPIRED") return;

    setIsLoading(true);
    try {
      const apiChar = getApiChara();
      let apiUrl;
      const useDateWiseApi = ["DISCHARGED", "TRANSFERRED", "CANCELLED", "ALL"].includes(status);

      if (useDateWiseApi) {
        apiUrl = `${apiChar}/ipd/admissions/status/datewise?status=${status}&startDate=${start}&endDate=${end}`;
      } else {
        apiUrl = `${apiChar}/ipd/admissions/status/${status}`;
      }

      const response = await fetch(apiUrl, {
        headers: { Authorization: "Bearer " + token },
      });
      if (!response.ok) throw new Error("Failed to fetch patients");
      const data = await response.json();

      let result = data || [];
      // Apply client-side date filtering only for statuses that DON'T use the datewise API 
      // (like ACTIVE) and only when explicitly submitted.
      if (!useDateWiseApi && forceClientFilter) {
        result = data.filter(adm => {
          const adDate = adm.admissionDateTime ? adm.admissionDateTime.split(/[ T]/)[0] : "";
          return adDate >= start && adDate <= end;
        });
      }

      setPatients(result || []);
    } catch (err) {
      console.error(err);
      toast.error(t("Error fetching IPD patients"));
      setPatients([]);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    if (selectedPatientId) {
      fetchPatientAdmissions(selectedPatientId);
    } else if (["DISCHARGED", "TRANSFERRED", "CANCELLED", "ALL"].includes(statusFilter)) {
      const start = getSixMonthsAgoDate();
      const end = getTodayDate();
      setStartDate(start);
      setEndDate(end);
      fetchPatients(statusFilter, start, end, false);
    } else {
      fetchPatients(statusFilter, startDate, endDate, true);
    }
  }, [statusFilter, selectedPatientId]);

  const fetchPatientAdmissions = async (patientId) => {
    const token = getAuthToken();
    if (!token || token === "EXPIRED") return;

    setIsLoading(true);
    try {
      const apiChar = getApiChara();
      const apiUrl = `${apiChar}/ipd/admissions/patient/${patientId}`;
      const response = await fetch(apiUrl, {
        headers: { Authorization: "Bearer " + token },
      });
      if (!response.ok) throw new Error("Failed to fetch patient admissions");
      const data = await response.json();
      setPatients(data || []);
    } catch (err) {
      console.error(err);
      toast.error(t("Error fetching admissions for this patient"));
    } finally {
      setIsLoading(false);
    }
  };

  const handlePatientSearch = async (value) => {
    setPatientSearch(value);
    if (value.length < 2) {
      setMatchingPatients([]);
      return;
    }

    try {
      const token = getAuthToken();
      const apiChar = getApiChara();
      const apiUrl = `${apiChar}/patient/getPatientByFNameOrLNameOrMobile?value=${value}`;
      const response = await fetch(apiUrl, {
        headers: { Authorization: "Bearer " + token }
      });
      if (response.ok) {
        const data = await response.json();
        setMatchingPatients(data.slice(0, 5) || []); // Limit to 5 results
      }
    } catch (err) {
      console.error(err);
    }
  };

  const selectPatient = (patient) => {
    setSelectedPatientId(patient.patientId);
    setPatientSearch(`${patient.firstName} ${patient.lastName}`);
    setMatchingPatients([]);
  };

  const clearPatientFilter = () => {
    setSelectedPatientId(null);
    setPatientSearch("");
    setMatchingPatients([]);
    fetchPatients(statusFilter, startDate, endDate, true);
  };

  const handleFilterSubmit = () => {
    const start = new Date(startDate);
    const end = new Date(endDate);
    const diffTime = Math.abs(end - start);
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    if (diffDays > 180) {
      toast.warning(t("Please select dates within the 180 days."));
      return;
    }
    fetchPatients(statusFilter, startDate, endDate, true);
  };

  const generatePDF = () => {
    const pdfDoc = new jsPDF(selectedOrientation, "pt", selectedPageSize);
    const pageWidth = pdfDoc.internal.pageSize.getWidth();

    const startY = drawReportHeader(pdfDoc, hospitalData, `IPD Patient List - ${statusFilter}`, { margin: 0, top: 0, hideSeparator: true, isExport: true });

    // 2. Metadata / Filter info
    pdfDoc.setFontSize(10);
    pdfDoc.setTextColor(100, 100, 100);
    pdfDoc.text(`Period: ${startDate} To ${endDate}`, 40, startY + 5);
    pdfDoc.text(`Total Records: ${patients.length}`, 40, startY + 20);
    pdfDoc.text(`Generated on: ${new Date().toLocaleString()}`, pageWidth - 40, startY + 5, { align: "right" });

    // 3. Table Generation
    pdfDoc.autoTable({
      head: [["IPD No", "Name", "Gender", "Phone", "Doctor", "Floor", "Ward/Bed", "Date", "Status"]],
      body: patients.map((admission) => [
        admission.ipdAdmissionId || "-",
        admission.patientId ? `${admission.patientId.firstName} ${admission.patientId.lastName}` : "-",
        admission.patientId?.gender || "-",
        admission.patientId?.mobile || "-",
        admission.doctorId?.firstName || "-",
        admission.wardBed?.floor || "-",
        admission.wardBed ? `${admission.wardBed.wardName} - ${admission.wardBed.bedNumber}` : "-",
        admission.admissionDateTime ? admission.admissionDateTime.split(/[ T]/)[0] : "-",
        admission.status || "-"
      ]),
      startY: startY + 35,
      styles: {
        fontSize: 9,
        cellPadding: 6,
        lineColor: [0, 0, 0],
        lineWidth: 0.1,
      },
      headStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        halign: 'center',
        lineWidth: 0.1,
        lineColor: [0, 0, 0]
      },
      alternateRowStyles: {
        fillColor: [248, 248, 248]
      },
      footStyles: {
        fillColor: [255, 255, 255],
        textColor: [0, 0, 0],
        fontStyle: 'bold',
        lineWidth: 0.1,
        lineColor: [0, 0, 0]
      },
      columnStyles: {
        0: { halign: 'center' },
        6: { halign: 'center' },
        7: { halign: 'center' }
      }
    });

    drawAuthorizedSignatory(pdfDoc, hospitalData);

    // 4. Footer
    const pageCount = pdfDoc.internal.getNumberOfPages();
    pdfDoc.setFontSize(9);
    for (let i = 1; i <= pageCount; i++) {
      pdfDoc.setPage(i);
      pdfDoc.text(`Page ${i} Of ${pageCount}`, pageWidth / 2, pdfDoc.internal.pageSize.getHeight() - 20, { align: "center" });
    }

    return pdfDoc;
  };

  const onExportHandler = () => {
    generatePDF().save(`IPD_Report_${statusFilter}_${getTodayDate()}.pdf`);
  };

  const onPreviewHandler = () => {
    const pdfBlob = generatePDF().output("blob");
    const pdfUrl = URL.createObjectURL(pdfBlob);
    setShowPreview(pdfUrl);
  };

  const onPrintHandler = () => {
    if (!patients || patients.length === 0) {
      alert(t("No data to print"));
      return;
    }

    // Create a hidden iframe for printing
    let printFrame = document.getElementById("printFrame");
    if (!printFrame) {
      printFrame = document.createElement("iframe");
      printFrame.id = "printFrame";
      printFrame.style.display = "none";
      document.body.appendChild(printFrame);
    }

    const tableClone = document.querySelector("#ipdPatientsTable").cloneNode(true);
    // Remove the last column (Action) from the clone
    tableClone.querySelectorAll("tr").forEach(tr => {
      if (tr.lastElementChild) tr.removeChild(tr.lastElementChild);
    });

    const printContent = `
      <html>
        <head>
          <title>${t("IPD Patient List")} - ${statusFilter}</title>
          <link href="https://cdn.jsdelivr.net/npm/bootstrap@5.3.0/dist/css/bootstrap.min.css" rel="stylesheet">
          <style>
            ${getReportStyles(selectedPageSize, selectedOrientation)}
          </style>
        </head>
        <body>
          ${getReportHeader(hospitalData, t("IPD Patient List"))}
          <div class="meta-info">
            <div><strong>${t("Period")}:</strong> ${startDate} ${t("To")} ${endDate}</div>
            <div><strong>${t("Total Records")}:</strong> ${patients.length}</div>
          </div>
          <div class="summary-badge" style="background: #f8fafc; padding: 10px 15px; border-radius: 8px; border-left: 4px solid #334155; margin-bottom: 20px; font-size: 13px;">
            <strong>${t("Summary")}:</strong> ${t("Total of")} ${patients.length} ${t("patients found under category")} <strong>${t(statusFilter)}</strong>.
          </div>
          ${tableClone.outerHTML}
        </body>
      </html>
    `;

    const frameDoc = printFrame.contentWindow.document;
    frameDoc.open();
    frameDoc.write(printContent);
    frameDoc.close();

    printFrame.contentWindow.focus();
    setTimeout(() => {
      printFrame.contentWindow.print();
    }, 500);
  };

  const handlePatientClick = (ipdAdmissionId) => {
    navigate(`/dashboard/ipd/${ipdAdmissionId}`);
  };

  const openDischargeModal = (admission) => {
    setSelectedAdmission(admission);
    setShowDischargeModal(true);
  };

  const openEditModal = (admission) => {
    setSelectedAdmission(admission);
    setShowAdmissionModal(true);
  };

  const openViewSummaryModal = (admission) => {
    setSelectedDischargedAdmission(admission);
    setShowViewSummaryModal(true);
  };

  const handleDischargeSubmit = async (formData) => {
    setIsLoading(true);
    try {
      const apiChar = getApiChara();
      const id = selectedAdmission.ipdAdmissionId;
      const apiUrl = `${apiChar}/ipd/admissions/${id}/discharge`;
      const token = getAuthToken();

      const formatDateTime = (val) => {
        if (!val) return null;
        if (val.length === 16) return val.replace("T", " ") + ":00";
        return val.replace("T", " ").replace(/\..*$/, "").replace("Z", "");
      };

      const payload = {
        ...selectedAdmission,
        status: "DISCHARGED",
        dischargeDateTime: formatDateTime(`${formData.dod}T${formData.dodTime}`),
        notes: formData.notes || selectedAdmission.notes,
        drWrittenDischargeSummery: formData.drWrittenDischargeSummery || "",
        pho: formData.pho,
        co: formData.co,
        kco: formData.kco,
        cf: formData.cf,
        nextAppointmentDate: formData.nextAppointment,
        dischargeType: formData.dischargeType,
        conditionAtDischarge: formData.condition,
        weight: formData.weight
      };

      const response = await fetch(apiUrl, {
        method: "PUT",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(payload)
      });

      if (!response.ok) throw new Error("Failed to discharge patient");

      toast.success(t("Patient discharged successfully!"));
      setShowDischargeModal(false);
      fetchPatients();
    } catch (err) {
      console.error(err);
      toast.error(t("Error discharging patient: ") + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  const mapToModalData = (adm) => {
    if (!adm) return null;

    const dateTimeStr = adm.admissionDateTime || "";
    let adDate = "";
    let adTime = "";
    if (dateTimeStr.includes("T")) {
      [adDate, adTime] = dateTimeStr.split("T");
    } else if (dateTimeStr.includes(" ")) {
      [adDate, adTime] = dateTimeStr.split(" ");
    }
    if (adTime && adTime.length > 5) adTime = adTime.slice(0, 5);

    return {
      name: `${adm.patientId?.firstName} ${adm.patientId?.lastName}`,
      gender: adm.patientId?.gender,
      dob: adm.patientId?.dob,
      age: adm.patientId?.age,
      mobile: adm.patientId?.mobile,
      address: adm.patientId?.address,
      patientId: adm.patientId?.patientId,
      admissionDate: adDate,
      admissionTime: adTime,
      admissionReason: adm.admissionReason,
      diagnosis: adm.diagnosis,
      notes: adm.notes,
      pho: adm.pho,
      co: adm.co,
      kco: adm.kco,
      cf: adm.cf,
      nextAppointment: adm.nextAppointmentDate || adm.nextFollowupVisitToDr,
      vitals: {
        weight: adm.weight ? `${adm.weight} kg` : ""
      }
    };
  };

  return (
    <div>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h2 className={classes.pageTitle} style={{ margin: 0 }}>{t("IPD Patient List")}</h2>
      </div>

      {/* Consolidated Filters Row */}
      <div className="mb-4 bg-light p-3 rounded shadow-sm border">
        <div className="d-flex flex-wrap align-items-end gap-3">
          {/* Date Filters */}
          <div style={{ minWidth: '150px' }}>
            <label className="fw-bold small mb-1 d-block">{t("Start Date:")}</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={startDate}
              onChange={(e) => setStartDate(e.target.value)}
            />
          </div>
          <div style={{ minWidth: '150px' }}>
            <label className="fw-bold small mb-1 d-block">{t("End Date:")}</label>
            <input
              type="date"
              className="form-control form-control-sm"
              value={endDate}
              onChange={(e) => setEndDate(e.target.value)}
            />
          </div>
          <Button className="btn-premium" size="sm" onClick={handleFilterSubmit}>
            <FaPlay className="me-1" /> {t("Submit")}
          </Button>

          {/* Patient Search */}
          <div className="position-relative flex-grow-1" style={{ maxWidth: '300px' }}>
            <label className="fw-bold small mb-1 d-block">{t("Search Patient:")}</label>
            <div className="input-group input-group-sm">
              <input
                type="text"
                className="form-control"
                placeholder={t("Name / Mobile")}
                value={patientSearch}
                onChange={(e) => handlePatientSearch(e.target.value)}
              />
              {patientSearch && (
                <Button variant="outline-secondary" size="sm" onClick={clearPatientFilter}>
                  <FaTimes />
                </Button>
              )}
            </div>
            {matchingPatients.length > 0 && (
              <div className="position-absolute w-100 bg-white border rounded shadow-sm" style={{ zIndex: 1000, top: '100%' }}>
                {matchingPatients.map(p => (
                  <div
                    key={p.patientId}
                    className="p-2 border-bottom small cursor-pointer"
                    style={{ cursor: 'pointer' }}
                    onClick={() => selectPatient(p)}
                    onMouseEnter={(e) => e.target.style.backgroundColor = '#f8f9fa'}
                    onMouseLeave={(e) => e.target.style.backgroundColor = 'white'}
                  >
                    {p.firstName} {p.lastName} ({p.mobile})
                  </div>
                ))}
              </div>
            )}
          </div>

          {/* Status Filter */}
          <div style={{ minWidth: '150px' }}>
            <label className="fw-bold small mb-1 d-block">{t("Status:")}</label>
            <select
              className="form-select form-select-sm"
              value={statusFilter}
              onChange={(e) => setStatusFilter(e.target.value)}
            >
              <option value="ACTIVE">{t("ACTIVE")}</option>
              <option value="ALL">{t("ALL")}</option>
              <option value="DISCHARGED">{t("DISCHARGED")}</option>
              <option value="CANCELLED">{t("CANCELLED")}</option>
              <option value="TRANSFERRED">{t("TRANSFERRED")}</option>
            </select>
          </div>

        </div>
      </div>

      <div className="printData boxes table-responsive">
        <Table id="ipdPatientsTable" striped bordered hover responsive>
          <thead>
            <tr>
              <th>{t("IPD No")}</th>
              <th>{t("Name")}</th>
              <th>{t("Gender")}</th>
              <th>{t("Phone")}</th>
              <th>{t("Doctor")}</th>
              <th>{t("Floor")}</th>
              <th>{t("Ward/Bed")}</th>
              <th>{t("Admission Date")}</th>
              <th>{t("Status")}</th>
              <th className="no-print">{t("Action")}</th>
            </tr>
          </thead>
          <tbody>
            {isLoading ? (
              <tr>
                <td colSpan="10" className="text-center">
                  <Spinner animation="border" size="sm" /> {t("Loading...")}
                </td>
              </tr>
            ) : patients.length > 0 ? (
              patients.map((admission) => (
                <tr key={admission.ipdAdmissionId}>
                  <td>
                    <NavLink
                      to={`/dashboard/ipd/${admission.ipdAdmissionId}`}
                      className={classes.patientLink}
                    >
                      {admission.ipdAdmissionId}
                    </NavLink>
                  </td>
                  <td>
                    {admission.patientId
                      ? `${admission.patientId.firstName} ${admission.patientId.lastName}`
                      : "-"}
                  </td>
                  <td>{t(admission.patientId?.gender) || "-"}</td>
                  <td>{admission.patientId?.mobile || "-"}</td>
                  <td>{admission.doctorId?.firstName || "-"}</td>
                  <td> {admission.wardBed
                    ? `${admission.wardBed.floor}`
                    : "-"}</td>
                  <td>
                    {admission.wardBed
                      ? `${admission.wardBed.wardName} - ${admission.wardBed.bedNumber}`
                      : "-"}
                  </td>
                  <td>
                    {admission.admissionDateTime ? admission.admissionDateTime.split(/[ T]/)[0] : "-"}
                  </td>
                  <td>{t(admission.status)}</td>
                  <td className="no-print">
                    <div className="d-flex gap-2 justify-content-center align-items-center">
                      {!["TRANSFERRED", "DISCHARGED", "CANCELLED"].includes(admission.status) && (
                        <Button
                          className="btn-outline-premium p-1 d-flex align-items-center justify-content-center"
                          size="sm"
                          onClick={() => {
                            setSelectedAdmission(admission);
                            setShowAdmissionModal(true);
                          }}
                          title={t("Edit")}
                        >
                          <FaEdit />
                        </Button>
                      )}
                      {admission.status === "ACTIVE" && (
                        <Button
                          className="btn-outline-danger-premium py-1 px-2 d-flex align-items-center gap-1"
                          size="sm"
                          onClick={() => openDischargeModal(admission)}
                          title={t("Discharge Patient")}
                        >
                          <FaSignOutAlt /> <span style={{ fontSize: '0.75rem' }}>{t("Discharge")}</span>
                        </Button>
                      )}
                      {(admission.status === "DISCHARGED" || admission.status === "CANCELLED" || admission.status === "TRANSFERRED") && (
                        <Button
                          className="btn-outline-premium py-1 px-2 d-flex align-items-center gap-1"
                          size="sm"
                          onClick={() => openViewSummaryModal(admission)}
                          title={t("View Details")}
                        >
                          <FaEye /> <span style={{ fontSize: '0.75rem' }}>{t("View")}</span>
                        </Button>
                      )}
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td colSpan="11" className="text-center text-danger fw-bold">
                  {t("Record not found")}
                </td>
              </tr>
            )}
          </tbody>
        </Table>
      </div>

      {/* Desktop Only Bottom Actions */}
      <div className={`${classes.desktopOnly} ${classes.bottomActions}`}>
        <Button className="btn-outline-premium" size="sm" onClick={onPreviewHandler}>
          <FaEye className="me-1" /> {t("Preview")}
        </Button>
        <Button className="btn-outline-premium" size="sm" onClick={onExportHandler}>
          <FaFilePdf className="me-1" /> {t("Export")}
        </Button>
        <Button className="btn-outline-premium" size="sm" onClick={onPrintHandler}>
          <FaPrint className="me-1" /> {t("Print")}
        </Button>
      </div>

      {/* Preview Modal */}
      {showPreview && (
        <div className="modal fade show" style={{ display: "block", backgroundColor: "rgba(0,0,0,0.5)" }} tabIndex="-1">
          <div className="modal-dialog modal-lg">
            <div className="modal-content">
              <div className="modal-header">
                <h5 className="modal-title">{t("PDF Preview")}</h5>
                <button type="button" className="btn-close" onClick={() => setShowPreview(null)}></button>
              </div>
              <div className="modal-body p-0">
                <iframe title="PDF Preview" src={showPreview} width="100%" height="600" frameBorder="0"></iframe>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Discharge Modal */}
      <DischargeModal
        show={showDischargeModal}
        onHide={() => setShowDischargeModal(false)}
        onClose={() => setShowDischargeModal(false)}
        patient={mapToModalData(selectedAdmission)}
        onSubmit={handleDischargeSubmit}
        loading={isLoading}
      />

      {/* Edit/Delete Modal */}
      <AdmissionDetailsModal
        show={showAdmissionModal}
        onHide={() => {
          setShowAdmissionModal(false);
          fetchPatients();
        }}
        admissionData={selectedAdmission}
        patientId={selectedAdmission?.patientId?.patientId}
      />

      {/* Discharge Summary View Modal */}
      <DischargeSummaryViewModal
        show={showViewSummaryModal}
        onHide={() => setShowViewSummaryModal(false)}
        admission={selectedDischargedAdmission}
        hospitalData={hospitalData}
      />

      {/* Admission Status Notes Modal for Cancelled/Transferred */}
      <AdmissionStatusNotesModal
        show={showStatusNotesModal}
        onHide={() => setShowStatusNotesModal(false)}
        admission={selectedDischargedAdmission}
      />
    </div>
  );
};

export default IPDPatientList;
